<template>
  <div>
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white overflow-hidden">
      <div class="absolute inset-0 bg-black opacity-20"></div>
      <div class="container-custom section-padding relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional CW Home Improvement Services
            </h1>
            <p class="text-xl md:text-2xl mb-8 text-primary-100">
              Your trusted partner for all home improvments, repair, and maintenance needs. Committed to quality
              workmanship.
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <router-link to="/services"
                class="btn-secondary bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600">
                View Services
              </router-link>
            </div>
          </div>
          <div class="relative">
            <div class="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div class="grid grid-cols-2 gap-6">
                <div class="text-center">
                  <div class="text-3xl font-bold text-primary-200">100+</div>
                  <div class="text-sm text-primary-100">Happy Customers</div>
                </div>
                <div class="text-center">
                  <div class="text-3xl font-bold text-primary-200">5+</div>
                  <div class="text-sm text-primary-100">Years Experience</div>
                </div>
                <div class="text-center">
                  <div class="text-3xl font-bold text-primary-200">100%</div>
                  <div class="text-sm text-primary-100">Satisfaction</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Overview -->
    <section class="section-padding bg-white">
      <div class="container-custom">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Our Services
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            From minor repairs to major renovations, we handle all your home maintenance needs with professionalism and
            expertise.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div class="card group">
            <div
              class="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary-200 transition-colors">
              <svg class="w-8 h-8 text-primary-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5v14"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 5v14"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Flooring</h3>
            <p class="text-gray-600 mb-4">
              Hardwood, laminate, vinyl, tile, and carpet installation. Professional flooring solutions for every room.
            </p>
            <router-link to="/services" class="text-primary-800 hover:text-primary-500 font-medium">
              Learn More →
            </router-link>
          </div>

          <div class="card group">
            <div
              class="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary-200 transition-colors">
              <svg class="w-8 h-8 text-primary-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Drywall Repair</h3>
            <p class="text-gray-600 mb-4">
              Hole repairs, water damage restoration, new drywall installation, and professional finishing.
            </p>
            <router-link to="/services" class="text-primary-800 hover:text-primary-500 font-medium">
              Learn More →
            </router-link>
          </div>

          <div class="card group">
            <div
              class="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary-200 transition-colors">
              <svg class="w-8 h-8 text-primary-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Trimming & Molding</h3>
            <p class="text-gray-600 mb-4">
              Baseboards, crown molding, door and window casing, chair rails, and custom trim work.
            </p>
            <router-link to="/services" class="text-primary-800 hover:text-primary-500 font-medium">
              Learn More →
            </router-link>
          </div>

          <div class="card group">
            <div
              class="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary-200 transition-colors">
              <svg class="w-8 h-8 text-primary-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Painting</h3>
            <p class="text-gray-600 mb-4">
              Interior and exterior painting, color consultation, surface preparation, and cabinet refinishing.
            </p>
            <router-link to="/services" class="text-primary-800 hover:text-primary-500 font-medium">
              Learn More →
            </router-link>
          </div>

          <div class="card group">
            <div
              class="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary-200 transition-colors">
              <svg class="w-8 h-8 text-primary-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Home Remodeling</h3>
            <p class="text-gray-600 mb-4">
              Kitchen and bathroom renovations, room additions, basement finishing, and complete home transformations.
            </p>
            <router-link to="/services" class="text-primary-800 hover:text-primary-500 font-medium">
              Learn More →
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us -->
    <section class="section-padding gradient-bg">
      <div class="container-custom">
        
          <div class="text-center mb-16">
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Choose CW Home Improvements?
            </h2>
          </div>
          
            <div class="space-y-6">
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 bg-primary-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">Experienced Personal</h3>
                  <p class="text-gray-600">5+ years of experience with skilled craftsmen and quality workmanship.</p>
                </div>
              </div>
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 bg-primary-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">Free Estimates</h3>
                  <p class="text-gray-600">Transparent pricing with free, no-obligation estimates for all projects.</p>
                </div>
              </div>
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 bg-primary-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">Satisfaction Guaranteed</h3>
                  <p class="text-gray-600">100% satisfaction guarantee with quality work and excellent customer service.
                  </p>
                </div>
              </div>
            </div>
        </div>
      
    </section>

    <!-- CTA Section -->
    <section class="section-padding bg-primary-800 text-white">
      <div class="container-custom text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-6">
          Ready to Get Started?
        </h2>
        <p class="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
          Contact us today for a free estimate and let us help you with all your home repair and maintenance needs.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
                      <a href="tel:5613983356"
            class="btn-secondary bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600">
            Call Now: (561) 398-3356
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Home'
}
</script> 